import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Bell, Bookmark, FileText, Link2, MessageCircle, Pin } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { ConnectionBanner } from "@/components/ConnectionBanner";
import { IconRail } from "@/components/IconRail";
import { MobileTabBar } from "@/components/MobileTabBar";
import { Sidebar } from "@/components/Sidebar";
import { ChannelHeader } from "@/components/ChannelHeader";
import { MessageList } from "@/components/MessageList";
import { MessageItem } from "@/components/MessageItem";
import { Composer } from "@/components/Composer";
import { ThreadPanel } from "@/components/ThreadPanel";
import { ProfilePanel } from "@/components/ProfilePanel";
import { HuddleBanner, HuddleWindow } from "@/components/HuddleWindow";
import { Modals } from "@/components/Modals";
import { Attachments } from "@/components/Attachments";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { useIsMobile } from "@/lib/device";
import { consumePushUrl, onServiceWorkerMessage, syncPush, trackPushOpen } from "@/lib/push";
import { fmtRelative } from "@/lib/format";
import { ThreadsView, HuddlesView, DraftsView, DirectoryView, ToolsView, FilesList } from "./views";
import { DMsList, ActivityList, SavedList, EmptyMain } from "./listViews";

const PinsTab = ({ conversation }) => {
  const { usersByHandle, usersById, meId, openProfile, msgState } = useChat();
  const [pins, setPins] = useState([]);
  const ctx = useMemo(() => ({ usersByHandle, usersById, meId, onMentionClick: (u) => openProfile(u.id) }), [usersByHandle, usersById, meId, openProfile]);
  useEffect(() => { api.get(`/conversations/${conversation.id}/pins`).then((r) => setPins(r.data)).catch(() => {}); }, [conversation.id, msgState]);
  return (
    <div className="tc-view__body tc-scroll" data-testid="pins-tab">
      {!pins.length && <div className="tc-empty"><Pin /><h3>No pinned messages</h3><p>Pin important messages so everyone in {conversation.type === "channel" ? `#${conversation.name}` : "this conversation"} can find them quickly.</p></div>}
      {pins.map((m) => <div className="tc-card" key={m.id}><MessageItem m={m} ctx={ctx} compact hideThreadIndicator /></div>)}
    </div>
  );
};

const FilesTab = ({ conversation }) => {
  const { msgState } = useChat();
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  useEffect(() => {
    api.get(`/conversations/${conversation.id}/links`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/conversations/${conversation.id}/files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [conversation.id, msgState[conversation.id]?.messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="tc-view__body tc-scroll" data-testid="files-tab">
      {!items.length && !files.length && <div className="tc-empty"><FileText /><h3>No files or links yet</h3><p>Files and links shared in this conversation are collected here. Drag a file into the conversation or use the + button in the message box.</p></div>}
      {files.length > 0 && (
        <>
          <h3 className="tc-view__h3">Files</h3>
          <div className="tc-files-grid" data-testid="files-tab-files">
            {files.map((f) => <div key={f.id} className="tc-card" style={{ padding: 10, marginBottom: 0 }}><div style={{ fontSize: 12, color: "var(--tc-text-muted)", marginBottom: 6 }}>{f.user?.display_name} · {fmtRelative(f.created_at)}</div><Attachments files={[f]} compact /></div>)}
          </div>
        </>
      )}
      {items.length > 0 && <h3 className="tc-view__h3">Links</h3>}
      {items.map((m) => (
        <div className="tc-card" key={m.id} style={{ padding: 12 }}>
          <div style={{ fontSize: 13, color: "var(--tc-text-muted)", marginBottom: 4 }}>{m.user?.display_name} · {new Date(m.created_at).toLocaleString()}</div>
          {m.links.map((l) => <a key={l} href={l} target="_blank" rel="noreferrer" className="tc-link-pill" style={{ display: "flex" }}><Link2 size={14} /> {l}</a>)}
        </div>
      ))}
    </div>
  );
};

const ConversationMain = ({ cid, onBack }) => {
  const { current, sendMessage, conversations } = useChat();
  const [tab, setTab] = useState("messages");
  useEffect(() => setTab("messages"), [cid]);
  const c = current || conversations.find((x) => x.id === cid);
  if (!c) return <div className="tc-main" data-testid="conversation-loading" />;
  return (
    <main className="tc-main" data-testid="conversation-main">
      <ChannelHeader conversation={c} tab={tab} setTab={setTab} onBack={onBack} />
      <HuddleBanner conversationId={c.id} />
      {tab === "messages" ? (
        <>
          <MessageList conversation={c} />
          <Composer conversationId={c.id} initialText={c.draft || ""} autoFocus onSend={(text, fileIds) => sendMessage(c.id, text, null, fileIds)} />
        </>
      ) : tab === "pins" ? <PinsTab conversation={c} /> : <FilesTab conversation={c} />}
    </main>
  );
};

export default function ClientPage() {
  const { "*": splat = "" } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { conversations, selectConversation, jumpToMessage, panel, setModal, myHuddleId } = useChat();
  const seg = splat.split("/").filter(Boolean);
  const isId = (s) => /^[a-f0-9]{24}$/.test(s || "");
  const view = isId(seg[0]) ? "home" : seg[0] || "home";
  const cid = isId(seg[0]) ? seg[0] : isId(seg[1]) ? seg[1] : null;
  const midParam = new URLSearchParams(search).get("m");
  const mid = isId(midParam) ? midParam : null;
  const lastJump = useRef(null);
  const listViews = ["dms", "activity", "later", "files"];
  const railView = [...listViews, "huddles", "tools"].includes(view) ? view : "home";
  const isMobile = useIsMobile();
  const atHomeList = seg[0] === "home";

  useEffect(() => {
    if (isMobile && !seg.length) { navigate("/client/home", { replace: true }); return; }
    if (view === "home" && !cid && conversations.length && !(isMobile && atHomeList)) {
      const last = localStorage.getItem("tc_last_conv");
      const target = conversations.find((c) => c.id === last) || conversations.find((c) => c.name === "general") || conversations[0];
      if (target) navigate(`/client/${target.id}`, { replace: true });
    }
  }, [view, cid, conversations, navigate, isMobile, atHomeList, seg.length]);

  useEffect(() => {
    lastJump.current = mid ? `${cid}:${mid}` : null;
    selectConversation(cid, mid);
    if (cid && view === "home") localStorage.setItem("tc_last_conv", cid);
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cid || !mid || lastJump.current === `${cid}:${mid}`) return;
    lastJump.current = `${cid}:${mid}`;
    jumpToMessage(cid, mid);
  }, [mid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { analytics.track("page_view", { category: "navigation", label: `/client/${splat}`, target: view }); }, [splat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setModal({ type: "search" }); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setModal]);

  useEffect(() => {
    trackPushOpen();
    syncPush();
    return onServiceWorkerMessage((msg) => {
      if (msg.type === "tc:navigate" && msg.url) navigate(consumePushUrl(msg.url));
      if (msg.type === "tc:resubscribe") syncPush();
    });
  }, [navigate]);

  const openFromList = (m) => { const c = m.conversation_id || m.conversation?.id; navigate(m.id ? `/client/${view}/${c}?m=${m.id}` : `/client/${view}/${c}`); };
  const onBack = () => navigate(view === "home" ? "/client/home" : `/client/${view}`);

  const left = view === "dms" ? <DMsList activeId={cid} onOpen={(id) => navigate(`/client/dms/${id}`)} />
    : view === "activity" ? <ActivityList onOpen={openFromList} />
    : view === "later" ? <SavedList onOpen={openFromList} />
    : view === "files" ? <FilesList onOpen={openFromList} />
    : <Sidebar view={view} />;

  const main = cid ? <ConversationMain cid={cid} onBack={isMobile ? onBack : undefined} />
    : view === "threads" ? <main className="tc-main"><ThreadsView /></main>
    : view === "huddles" ? <main className="tc-main"><HuddlesView /></main>
    : view === "drafts" ? <main className="tc-main"><DraftsView /></main>
    : view === "directories" ? <main className="tc-main"><DirectoryView /></main>
    : view === "tools" ? <main className="tc-main"><ToolsView /></main>
    : view === "dms" ? <EmptyMain icon={MessageCircle} title="Direct messages" text="Pick a conversation on the left or start a new one." />
    : view === "activity" ? <EmptyMain icon={Bell} title="Activity" text="Mentions, replies and reactions to your messages." />
    : view === "later" ? <EmptyMain icon={Bookmark} title="Later" text="Messages you've saved for later live here." />
    : view === "files" ? <EmptyMain icon={FileText} title="Files & links" text="Select an item to jump to it in the conversation." />
    : <div className="tc-main" />;

  const hasOwnMain = ["threads", "huddles", "drafts", "directories", "tools"].includes(view);
  const showLeft = !isMobile || (!cid && !hasOwnMain);
  const showMain = !isMobile || cid || hasOwnMain;

  return (
    <div className={`tc-app${isMobile ? " is-mobile" : ""}${myHuddleId ? " has-huddle" : ""}`} data-testid="client-page" data-layout={isMobile ? "mobile" : "desktop"}>
      <TopBar />
      <ConnectionBanner />
      <div className="tc-body">
        <IconRail view={railView} />
        {showLeft && <div className="tc-leftwrap" style={{ position: "relative", display: "flex", flexShrink: 0 }}>{left}<HuddleWindow /></div>}
        {!showLeft && <HuddleWindow />}
        {showMain && main}
        {panel?.type === "thread" && <ThreadPanel parentId={panel.id} />}
        {panel?.type === "profile" && <ProfilePanel userId={panel.userId} />}
      </div>
      {isMobile && <MobileTabBar view={railView} />}
      <Modals />
    </div>
  );
}
